import {
  createContext, FC, ReactNode, useCallback, useContext, useMemo, useReducer,
} from 'react';
import { noop } from 'src/common/helpers';
import type { EditorListing } from 'src/common/types/api/listings';
import { useRouter } from 'next/router';
import { stringify } from 'querystring';
import { ListingPagination } from 'src/admin/common/utils/serverGetEditorListings';
import bulkDeleteListings from '../utils/api/bulkDeleteListings';
import bulkPublishListings from '../utils/api/bulkPublishListings';
import bulkUnpublishListings from '../utils/api/bulkUnpublishListings';
import { isListingValid } from '../utils/getListingProgress';

// Actions
const SELECT_LISTING = 'SELECT_LISTING';
const DESELECT_LISTING = 'DESELECT_LISTING';
const SELECT_ALL_LISTINGS = 'SELECT_ALL_LISTINGS';
const DESELECT_ALL_LISTINGS = 'DESELECT_ALL_LISTINGS';

// Actions Creators
const selectListingAction = (listingId: string) => ({
  type: SELECT_LISTING,
  listingId,
} as const);

const deselectListingAction = (listingId: string) => ({
  type: DESELECT_LISTING,
  listingId,
} as const);

const selectAllListingsAction = (listingIds: string[]) => ({
  type: SELECT_ALL_LISTINGS,
  listingIds,
} as const);

const deselectAllListingsAction = () => ({
  type: DESELECT_ALL_LISTINGS,
} as const);

export type NoListingState = 'search' | 'add' | '';

export type Sort = 'asc' | 'desc' | 'new' | 'old' | 'snzd' | 'pub' | 'unpub';

// Types Definition
type ProviderProps = {
  children: ReactNode;
  value: {
    listings: EditorListing[];
    pagination: ListingPagination;
    search: string;
    sort: Sort;
    isImpersonatedSession?: boolean;
  };
};

type ListingsPageContext = {
  search: string;
  sort: Sort;
  ownerListings: EditorListing[];
  publishedListings: EditorListing[];
  selectedPublishedListingIds: string[];
  pagination: ListingPagination;
  selectedListings: string[];
  allSelected: boolean;
  noListingState: NoListingState;
  selectListing: (listingId: string) => void;
  deselectListing: (listingId: string) => void;
  selectAllListings: () => void;
  deselectAllListings: () => void;
  publishSelectedListings: () => void;
  unpublishSelectedListings: () => void;
  deleteSelectedListings: () => void;
  publishListing: (listingId: string) => void;
  unpublishListing: (listingId: string) => void;
  deleteListing: (listing: string) => void;
  setSearch: (query: string) => void;
  setSort: (sort: Sort) => void;
  setPage: (page: number) => void;
  refresh: () => void;
  getListingPaginatedUrl: ({ page, sort, search }: {
    page?: number; sort?: Sort; search?: string;
  }) => string;
  isImpersonatedSession: boolean;
};

type ListingsPageState = {
  sort: Sort;
  search: string;
  selectedListings: string[];
};

type ListingsPageActions =
ReturnType<typeof selectListingAction> |
ReturnType<typeof deselectListingAction> |
ReturnType<typeof selectAllListingsAction> |
ReturnType<typeof deselectAllListingsAction>;

// Initial contexts and states
const initialContext: ListingsPageContext = {
  search: '',
  sort: 'new',
  ownerListings: [],
  publishedListings: [],
  selectedPublishedListingIds: [],
  selectedListings: [],
  noListingState: '',
  allSelected: false,
  pagination: null,
  isImpersonatedSession: false,
  selectListing: noop,
  deselectListing: noop,
  selectAllListings: noop,
  deselectAllListings: noop,
  publishSelectedListings: noop,
  unpublishSelectedListings: noop,
  deleteSelectedListings: noop,
  publishListing: noop,
  unpublishListing: noop,
  deleteListing: noop,
  setSearch: noop,
  setSort: noop,
  setPage: noop,
  refresh: noop,
  getListingPaginatedUrl: () => '',
};

const getInitialState = (
  sort: Sort,
  search: string,
): ListingsPageState => ({
  sort,
  search,
  selectedListings: [],
});

// Reducer
const listingsPageReducer = (
  state: ListingsPageState,
  action: ListingsPageActions,
): ListingsPageState => {
  switch (action.type) {
    case SELECT_LISTING: {
      return {
        ...state,
        selectedListings: [...state.selectedListings, action.listingId],
      };
    }
    case DESELECT_LISTING: {
      return {
        ...state,
        selectedListings: state.selectedListings.filter(
          (listingId: string) => listingId !== action.listingId,
        ),
      };
    }
    case SELECT_ALL_LISTINGS: {
      return {
        ...state,
        selectedListings: action.listingIds,
      };
    }
    case DESELECT_ALL_LISTINGS: {
      return {
        ...state,
        selectedListings: [],
      };
    }
    default:
      return state;
  }
};

const listingsPageContext = createContext<ListingsPageContext>(initialContext);

type PaginatedQuery = {
  page?: number;
  sort?: Sort;
  search?: string;
};

const ListingsPageProvider: FC<ProviderProps> = ({
  children, value: {
    listings: ownerListings,
    pagination,
    sort: sortQuery,
    search: searchQuery,
    isImpersonatedSession,
  },
}) => {
  const [state, dispatch] = useReducer(
    listingsPageReducer,
    {},
    () => getInitialState(sortQuery, searchQuery),
  );
  const {
    selectedListings,
  } = state;

  const { pathname, query, push } = useRouter();

  const getListingPaginatedUrl = useCallback(({ page, sort, search }: PaginatedQuery = {}) => {
    const sortFilter = sort ?? sortQuery;
    const searchFilter = search ?? searchQuery;
    const pageFilter = page ?? pagination.page;

    // Only retain any params not related to these filters
    const {
      sort: ignore1, search: ignore2, page: ignore3, ...extraQuery
    } = query;

    // Keep URL tidy by removing unused params
    return (
      `${pathname}?${stringify({
        ...extraQuery,
        ...(pageFilter && { page: pageFilter }),
        ...(sortFilter && { sort: sortFilter }),
        ...(searchFilter && { search: searchFilter }),
      })}`);
  }, [pathname, query, pagination.page, sortQuery, searchQuery]);

  const deselectAllListings = useCallback(() => dispatch(deselectAllListingsAction()), []);
  const gotoUrl = useCallback(({ page, sort, search }: PaginatedQuery = {}) => {
    // Prevent the user from accidentally acting on previous selections
    deselectAllListings();
    push(getListingPaginatedUrl({ page, sort, search }));
  }, [getListingPaginatedUrl, push, deselectAllListings]);

  const refresh = useCallback(() => gotoUrl(), [gotoUrl]);

  let noListingState: NoListingState = ownerListings.length === 0 ? 'add' : '';
  if ((
    searchQuery.length
    || ['pub', 'unpub', 'snzd'].includes(sortQuery)
  )
  && ownerListings.length === 0) {
    noListingState = 'search';
  }

  const publishedListings = useMemo(() => (
    ownerListings.filter((listing) => listing.active)
  ), [ownerListings]);

  const selectedPublishedListingIds = useMemo(
    () => publishedListings
      .filter((listing) => selectedListings.includes(listing.id))
      .map((listing) => listing.id),
    [publishedListings, selectedListings],
  );

  const allSelected = ownerListings.length !== 0
  && ownerListings.length === selectedListings.length;
  const selectListing = useCallback(
    (listingId: string) => dispatch(selectListingAction(listingId)),
    [],
  );

  const deselectListing = useCallback(
    (listingId: string) => dispatch(deselectListingAction(listingId)),
    [],
  );

  const selectAllListings = useCallback(() => dispatch(
    selectAllListingsAction(ownerListings.map((listing: EditorListing) => listing.id)),
  ), [ownerListings]);

  const setSearch = useCallback((search: string) => {
    gotoUrl({ search, page: 1 }); // always show first page
  }, [gotoUrl]);

  const setSort = useCallback((sort: Sort) => {
    gotoUrl({ sort, page: 1 }); // always show first page since sort can also filter
  }, [gotoUrl]);

  const setPage = useCallback((page: number) => {
    gotoUrl({ page });
  }, [gotoUrl]);

  const allListingsValidity = ownerListings.map(
    (listing) => isListingValid(listing, isImpersonatedSession),
  );

  const publishSelectedListings = useCallback(async () => {
    const unpublishedListings = ownerListings.filter(
      (listing, i) => !listing.active
      && selectedListings.includes(listing.id)
      && !listing.under_review
      && allListingsValidity[i],
    );

    if (unpublishedListings.length > 0) {
      await bulkPublishListings(unpublishedListings);
      deselectAllListings();
    }

    refresh();
  }, [ownerListings, refresh, selectedListings, allListingsValidity, deselectAllListings]);

  const unpublishSelectedListings = useCallback(async () => {
    const selectedPublishedListings = ownerListings.filter(
      (listing) => listing.active && selectedListings.includes(listing.id),
    );

    if (selectedPublishedListings.length > 0) {
      await bulkUnpublishListings(selectedPublishedListings);
      deselectAllListings();
    }

    refresh();
  }, [ownerListings, refresh, selectedListings, deselectAllListings]);

  const deleteSelectedListings = useCallback(async () => {
    const listingsToBeDeleted = ownerListings.filter(
      (listing) => selectedListings.includes(listing.id),
    );
    await bulkDeleteListings(listingsToBeDeleted);
    refresh();
  }, [refresh, ownerListings, selectedListings]);

  const publishListing = useCallback(async (listingId: string) => {
    const listing = ownerListings.filter((l) => l.id === listingId)[0];
    await bulkPublishListings([listing]);
    refresh();
  }, [refresh, ownerListings]);

  const unpublishListing = useCallback(async (listingId: string) => {
    const listing = ownerListings.filter((l) => l.id === listingId)[0];
    await bulkUnpublishListings([listing]);
    refresh();
  }, [refresh, ownerListings]);

  const deleteListing = useCallback(async (listingId: string) => {
    const listing = ownerListings.filter((l) => l.id === listingId)[0];
    await bulkDeleteListings([listing]);
    refresh();
  }, [refresh, ownerListings]);

  const contextValue = useMemo(
    () => (
      {
        search: searchQuery, // SearchField handles own state
        sort: sortQuery,
        ownerListings,
        publishedListings,
        selectedListings,
        allSelected,
        noListingState,
        pagination,
        selectedPublishedListingIds,
        selectListing,
        deselectListing,
        selectAllListings,
        deselectAllListings,
        publishSelectedListings,
        unpublishSelectedListings,
        deleteSelectedListings,
        publishListing,
        unpublishListing,
        deleteListing,
        setSearch,
        setSort,
        setPage,
        refresh,
        getListingPaginatedUrl,
        isImpersonatedSession: !!isImpersonatedSession,
      }),
    [
      searchQuery,
      sortQuery,
      ownerListings,
      publishedListings,
      selectedListings,
      noListingState,
      pagination,
      selectedPublishedListingIds,
      selectListing,
      deselectListing,
      allSelected,
      selectAllListings,
      deselectAllListings,
      publishSelectedListings,
      unpublishSelectedListings,
      deleteSelectedListings,
      publishListing,
      unpublishListing,
      deleteListing,
      setSearch,
      setSort,
      setPage,
      refresh,
      getListingPaginatedUrl,
      isImpersonatedSession,
    ],
  );

  return (
    <listingsPageContext.Provider value={contextValue}>
      {children}
    </listingsPageContext.Provider>
  );
};

export default ListingsPageProvider;

export const useListingsPageContext = (): ListingsPageContext => {
  const context = useContext(listingsPageContext);

  if (!context) {
    throw new Error('useListingsPageContext must be used within a ListingsPageProvider');
  }

  return context;
};
